import { generations, label, orbit, type Generation, type Relation } from '../../shared/tree'
import type { FramilyPerson } from './framily'
import type { MyPerson } from './myPeople'
import type { PeopleGroup } from './peopleGroups'
import type { Tree, TreePerson } from './tree'

// What the People page's cards and list show, whichever people they come from: this circle's tree, or everyone
// you're connected to in all your circles, once each. Search, Who and Group by narrow and sort the same rows.
export type PeopleScope = 'circle' | 'framily'
export type PeopleBy = 'family' | 'circle' | 'name'
export type PeopleWho = 'everyone' | 'joined' | 'waiting' | 'memory'
export const PEOPLE_SCOPES: PeopleScope[] = ['circle', 'framily']
export const PEOPLE_BY: PeopleBy[] = ['family', 'circle', 'name']
export const PEOPLE_WHO: PeopleWho[] = ['everyone', 'joined', 'waiting', 'memory']

export type ShownPerson = {
  id: string
  name: string
  portrait: string | null
  pet: boolean
  living: boolean
  born: string | null
  died: string | null
  // A few words under their name: "Your sister", "You", or the circles they're in.
  line: string
  // How many of your circles they're in; 0 when it isn't known (someone in the app but not on the tree).
  circles: number
  // They've joined the app (their account is linked to them).
  joined: boolean
  // Their page, when they have one (someone who joined but isn't on the tree has none).
  href: string | null
  // The circles they're in, when known, for their profile.
  circleList?: { id: string; name: string }[]
  // Their account, when they have one and it's known, and whether you're connected one to one.
  userId?: string
  connected?: boolean
}
export type ShownGroup = { key: string; name: string; people: ShownPerson[] }

// Family grouping only makes sense inside one tree, and circle grouping across them.
export function groupingFor(scope: PeopleScope, by: PeopleBy): PeopleBy {
  if (by === 'name') return 'name'
  return scope === 'framily' ? 'circle' : 'family'
}

// How many circles each record is in, by the id of that record in any of them.
export function circleCounts(framily: FramilyPerson[]) {
  const counts = new Map<string, number>()
  for (const p of framily) for (const id of p.ids) counts.set(id, p.circles.length)
  return counts
}

const byName = (a: ShownPerson, b: ShownPerson) => a.name.localeCompare(b.name)
const youFirst = (a: ShownPerson, b: ShownPerson) => Number(b.line === 'You') - Number(a.line === 'You') || byName(a, b)

// This circle's tree, in the families read from it or A to Z. People who joined the app but aren't on the tree get
// their own group at the end, so no one in the circle goes missing.
export function fromTree(
  tree: Tree,
  families: PeopleGroup[],
  by: PeopleBy,
  line: (p: TreePerson) => string,
  counts: Map<string, number>,
  offTree: ShownPerson[],
  // The account linked to each person here, so their profile can open their own page.
  accounts: Map<string, string> = new Map(),
): ShownGroup[] {
  const shown = (p: TreePerson): ShownPerson => ({
    id: p.id,
    name: p.name,
    portrait: p.portrait,
    pet: p.pet,
    living: p.living,
    born: p.born,
    died: p.died,
    line: line(p),
    circles: counts.get(p.id) ?? 1,
    joined: p.linked,
    href: `/person/${p.id}`,
    userId: accounts.get(p.id),
  })
  const groups =
    by === 'name'
      ? [{ key: 'all', name: '', people: tree.people.map(shown).sort(youFirst) }]
      : families.map((g) => ({ key: g.key, name: g.name, people: g.people.map(shown) }))
  return offTree.length > 0
    ? [...groups, { key: 'off-tree', name: 'In the app, not on the tree yet', people: offTree }]
    : groups
}

// Everyone you're connected to (the default People view), one row a person: by circle (someone in two circles shows
// in both) or A to Z. Narrowed to one circle, just the people in it.
// relation says what someone is to you, by their record in the tree you're looking from ("Your brother").
export function fromMyPeople(
  people: MyPerson[],
  by: PeopleBy,
  circleId?: string,
  relation: (personId: string) => string = () => '',
): ShownGroup[] {
  const shown = (p: MyPerson, fallback: string): ShownPerson => {
    const line = (p.personId && relation(p.personId)) || fallback || (p.connected ? 'Connected' : '')
    return {
      id: p.key,
      name: p.name,
      portrait: p.portrait,
      pet: p.pet,
      living: p.living,
      born: p.born,
      died: p.died,
      line: p.me ? 'You' : line,
      circles: p.circles.length,
      joined: p.account,
      href: p.personId ? `/person/${p.personId}` : null,
      circleList: p.circles,
      userId: p.key.startsWith('u:') ? p.key.slice(2) : undefined,
      connected: !!p.connected,
    }
  }
  const here = circleId ? people.filter((p) => p.circles.some((c) => c.id === circleId)) : people
  if (by === 'name' || circleId)
    return [
      {
        key: 'all',
        name: '',
        people: here.map((p) => shown(p, circleId ? '' : p.circles.map((c) => c.name).join(', '))).sort(youFirst),
      },
    ]
  const circles = new Map<string, { name: string; people: ShownPerson[] }>()
  for (const p of here) {
    for (const c of p.circles) {
      const group = circles.get(c.id) ?? { name: c.name, people: [] }
      group.people.push(shown(p, ''))
      circles.set(c.id, group)
    }
  }
  const groups = [...circles].map(([id, g]) => ({ key: `circle-${id}`, name: g.name, people: g.people.sort(youFirst) }))
  // People you're connected to one to one, in none of your circles, have a group of their own.
  const direct = here.filter((p) => p.circles.length === 0).map((p) => shown(p, ''))
  return direct.length > 0
    ? [...groups, { key: 'connected', name: 'Connected', people: direct.sort(youFirst) }]
    : groups
}

// The faces on a circle's cover: those with a portrait first, then the rest, people before pets.
export function coverFaces(people: MyPerson[], circleId: string, n = 4) {
  return people
    .filter((p) => p.circles.some((c) => c.id === circleId))
    .sort((a, b) => Number(!!b.portrait) - Number(!!a.portrait) || Number(a.pet) - Number(b.pet))
    .slice(0, n)
}

const fits = (who: PeopleWho, p: ShownPerson) =>
  who === 'joined'
    ? p.joined
    : who === 'waiting'
      ? p.living && !p.pet && !p.joined
      : who === 'memory'
        ? !p.living
        : true

// The groups with only the people a search and Who let through; a group left empty goes.
export function narrow(groups: ShownGroup[], who: PeopleWho, search: string): ShownGroup[] {
  const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const found = (p: ShownPerson) => {
    const text = `${p.name} ${p.line}`.toLowerCase()
    return words.every((w) => text.includes(w))
  }
  return groups
    .map((g) => ({ ...g, people: g.people.filter((p) => fits(who, p) && found(p)) }))
    .filter((g) => g.people.length > 0)
}

// How each person on a tree is to you, under their name: "You", "Your sister".
export function relationTo(tree: Tree) {
  const near = tree.me ? orbit(tree.me, tree.links) : null
  const further = tree.me && near ? generations(tree.me, tree.links, near) : null
  const toYou = new Map<string, Relation | Generation>(
    [...Object.values(near ?? {}), ...Object.values(further ?? {})].flat().map((n) => [n.id, n.relation]),
  )
  return (p: TreePerson) => {
    if (p.id === tree.me) return 'You'
    const relation = toYou.get(p.id)
    return relation ? `Your ${label(relation, p.referAs)}` : ''
  }
}
